import { useEffect, useRef, useState } from "react";

import { SOCKET_URL } from "../config/config";

const useEventOddsSocket = (eventId, username = "guest") => {
  const [liveMarket, setLiveMarket] = useState(null);

  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const lastOddsSignatureRef = useRef("");

  useEffect(() => {
    if (!eventId) {
      setLiveMarket(null);
      return undefined;
    }

    const normalizedEventId = String(eventId);

    // const normalizedUsername = String(username);
    const normalizedUsername = username ? String(username) : "guest";

    let unmounted = false;
    let reconnectAttempts = 0;

    // Event change होने पर पुराना market
    // और signature clear करें।
    setLiveMarket(null);
    lastOddsSignatureRef.current = "";

    const connect = () => {
      if (unmounted) return;

      const ws = new WebSocket(SOCKET_URL);

      socketRef.current = ws;

      ws.onopen = () => {
        if (unmounted) {
          ws.close();
          return;
        }

        reconnectAttempts = 0;

        console.log("DETAIL SOCKET JOIN:", {
          event_id: String(eventId),

          username: normalizedUsername,
        });

        ws.send(
          JSON.stringify({
            action: "JOIN",
            data: {
              username: normalizedUsername,
              event_id: String(eventId),
            },
          }),
        );
      };

      ws.onmessage = (socketEvent) => {
        try {
          const message = JSON.parse(socketEvent.data);

          console.log("DETAIL SOCKET MESSAGE:", {
            joinedEventId: String(eventId),

            action: message?.action,

            receivedEventId: String(
              message?.data?.event_id ?? message?.data?.eventId ?? "",
            ),

            marketNames: (
              message?.data?.market_types ||
              message?.data?.marketTypes ||
              []
            ).map((market) => market?.market_name || market?.marketName),

            completeMessage: message,
          });

          if (message?.action !== "MARKET_UPDATE" || !message?.data) {
            return;
          }

          const receivedEventId = String(
            message.data.event_id ?? message.data.eventId ?? "",
          );

          if (receivedEventId !== normalizedEventId) {
            return;
          }

          const marketTypes =
            message.data.market_types || message.data.marketTypes || [];

          const matchOddsMarket = marketTypes.find((market) => {
            const marketName = String(
              market?.market_name || market?.marketName || "",
            )
              .trim()
              .toLowerCase();

            return marketName === "match odds" || marketName === "match_odds";
          });

          // Match Odds market नहीं मिला तो
          // state update नहीं करेंगे।
          if (!matchOddsMarket) {
            return;
          }

          const runners =
            matchOddsMarket.runners || matchOddsMarket.marketRunners || [];

          // केवल UI में इस्तेमाल होने वाली values
          // का signature बनाएंगे।
          const oddsSignature = JSON.stringify(
            runners.map((runner) => [
              runner.selection_id ?? runner.selectionId,

              runner.back_1_price ?? runner.backPrice,

              runner.back_1_size ?? runner.backSize,

              runner.lay_1_price ?? runner.layPrice,

              runner.lay_1_size ?? runner.laySize,

              runner.status,
            ]),
          );

          // Same odds दोबारा आएं तो React
          // component rerender नहीं होगा।
          if (oddsSignature === lastOddsSignatureRef.current) {
            return;
          }

          lastOddsSignatureRef.current = oddsSignature;

          setLiveMarket(message.data);
        } catch (error) {
          console.error("Odds socket parse error:", normalizedEventId, error);
        }
      };

      ws.onerror = (error) => {
        // onclose reconnect handle करेगा।
        console.error("Odds socket error:", normalizedEventId, error);
      };

      ws.onclose = () => {
        if (unmounted) return;

        reconnectAttempts += 1;

        const reconnectDelay = Math.min(5000, reconnectAttempts * 1000);

        reconnectTimerRef.current = setTimeout(connect, reconnectDelay);
      };
    };

    connect();

    return () => {
      unmounted = true;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);

        reconnectTimerRef.current = null;
      }

      const currentSocket = socketRef.current;

      if (currentSocket) {
        currentSocket.onopen = null;
        currentSocket.onmessage = null;
        currentSocket.onerror = null;
        currentSocket.onclose = null;

        if (
          currentSocket.readyState === WebSocket.OPEN ||
          currentSocket.readyState === WebSocket.CONNECTING
        ) {
          currentSocket.close();
        }

        socketRef.current = null;
      }
    };
  }, [eventId, username]);

  return liveMarket;
};

export default useEventOddsSocket;
